import { foldName } from "../lexicon/fold.js";
import { ARCHETYPE_STYLES, type ArchetypePattern } from "./archetypes.js";
import { isIsoInstant } from "./time.js";

/**
 * The identity of a shopping-list line, built from facts and never from wording, so a re-worded
 * archetype or a new locale cannot orphan a tick. `a.<grape>.<style>.<country>.<region>.<gap>` names
 * a wine style (an absent field is `_`; the gap is `m3` for three years older, `p2` for two years
 * younger than the first wine of its step) and `g.<glass>` a coarse line for a step that names only
 * a glass. Producer and absolute vintage are never part of it. The parser walks the string by hand
 * instead of matching a regular expression, so any input costs linear work.
 */

export const SHOP_KEY_PREFIX = "shop:";
/** `learn_items.key` holds 128 characters in all; the `shop:` prefix takes five. */
export const SHOP_LINE_KEY_MAX = 128 - SHOP_KEY_PREFIX.length;
const FIELD_MAX = 32;
const GLASS_MAX = 48;
const GAP_MAX = 99;
const ABSENT = "_";

export const shopKey = (lineKey: string): string => `${SHOP_KEY_PREFIX}${lineKey}`;

function isAlnum(code: number): boolean {
  return (code >= 48 && code <= 57) || (code >= 97 && code <= 122);
}

/** Letters and digits in single-hyphen runs, neither starting nor ending with a hyphen. */
function isSlug(text: string, max: number): boolean {
  if (text.length === 0 || text.length > max) return false;
  let previousHyphen = true;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code === 45) {
      if (previousHyphen) return false;
      previousHyphen = true;
    } else if (isAlnum(code)) {
      previousHyphen = false;
    } else {
      return false;
    }
  }
  return !previousHyphen;
}

function isCountryField(text: string): boolean {
  if (text.length !== 2) return false;
  return [0, 1].every((i) => {
    const code = text.charCodeAt(i);
    return code >= 97 && code <= 122;
  });
}

function isGapField(text: string): boolean {
  if (text === ABSENT) return true;
  if (text.length < 2 || text.length > 3 || (text[0] !== "m" && text[0] !== "p")) return false;
  const digits = text.slice(1);
  if (digits.startsWith("0")) return false;
  for (let i = 0; i < digits.length; i += 1) {
    const code = digits.charCodeAt(i);
    if (code < 48 || code > 57) return false;
  }
  return true;
}

const orAbsent = (field: string, valid: (text: string) => boolean): boolean =>
  field === ABSENT || valid(field);

/** `true` when `lineKey` follows the line-key grammar (the part after `shop:`). */
export function isShopLineKey(lineKey: string): boolean {
  if (lineKey.length > SHOP_LINE_KEY_MAX) return false;
  const parts = lineKey.split(".");
  if (parts[0] === "g") return parts.length === 2 && isSlug(parts[1] ?? "", GLASS_MAX);
  if (parts[0] !== "a" || parts.length !== 6) return false;
  const [, grape = "", style = "", country = "", region = "", gap = ""] = parts;
  const fields = [grape, style, country, region];
  return (
    orAbsent(grape, (text) => isSlug(text, FIELD_MAX)) &&
    orAbsent(style, (text) => (ARCHETYPE_STYLES as readonly string[]).includes(text)) &&
    orAbsent(country, isCountryField) &&
    orAbsent(region, (text) => isSlug(text, FIELD_MAX)) &&
    isGapField(gap) &&
    fields.some((field) => field !== ABSENT)
  );
}

/** A name folded to a key segment: accents and case gone, runs of other characters one hyphen. */
function segment(text: string | undefined): string {
  if (text === undefined) return ABSENT;
  const folded = foldName(text).split(" ").join("-").slice(0, FIELD_MAX).replace(/-+$/, "");
  return folded === "" ? ABSENT : folded;
}

function gapSegment(gap: number | undefined): string {
  if (gap === undefined || !Number.isInteger(gap) || gap === 0) return ABSENT;
  return `${gap < 0 ? "m" : "p"}${String(Math.min(GAP_MAX, Math.abs(gap)))}`;
}

/** The key of a wine style, or `undefined` when the pattern names no wine (producer and vintage alone do not). */
export function archetypeLineKey(pattern: ArchetypePattern, gap?: number): string | undefined {
  const style = (ARCHETYPE_STYLES as readonly string[]).includes(pattern.style ?? "")
    ? pattern.style
    : undefined;
  const country = /^[A-Za-z]{2}$/.test(pattern.country ?? "")
    ? (pattern.country ?? "").toLowerCase()
    : undefined;
  const fields = [
    segment(pattern.grape),
    style ?? ABSENT,
    country ?? ABSENT,
    segment(pattern.region),
  ];
  if (fields.every((field) => field === ABSENT)) return undefined;
  return `a.${fields.join(".")}.${gapSegment(gap)}`;
}

/** The key of the coarse line for a glass slug, or `undefined` when it is not a slug. */
export function glassLineKey(glass: string): string | undefined {
  return isSlug(glass, GLASS_MAX) ? `g.${glass}` : undefined;
}

/** Value of `shop:<line key>`: one boolean, with the time it was set. */
export interface ShopValue {
  readonly have: boolean;
  readonly at?: string;
}

/** Reads a stored tick leniently: anything that is not a boolean is no tick at all. */
export function readShopValue(value: unknown): ShopValue | undefined {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return undefined;
  const have: unknown = Reflect.get(value, "have");
  const at: unknown = Reflect.get(value, "at");
  if (typeof have !== "boolean") return undefined;
  return isIsoInstant(at) ? { have, at } : { have };
}
